import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Divider,
  Button,
  IconButton,
  Paper,
  Modal,
} from "@mui/material";
import { Add, Delete, Edit } from "@mui/icons-material";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { uploadImage } from "services/imageUpload";
import { IMAGE_URL } from "network";

const GroupItemsSection = ({
  groupItems = [],
  onChange,
  deletedFiles = [],
  setDeletedFiles,
  error,
  touched,
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [newItem, setNewItem] = useState({
    name: "",
    shortDescription: "",
    image: "",
    servicePrice: "",
    productPrice: 0,
    duration: "",
    off_percentage: "",
  });
  const [previewImage, setPreviewImage] = useState("");
  const [editIndex, setEditIndex] = useState(null);
  const sliderRef = useRef(null);

  const sliderSettings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    responsive: [
      { breakpoint: 1024, settings: { slidesToShow: 2 } },
      { breakpoint: 600, settings: { slidesToShow: 1 } },
    ],
  };

  const openAddModal = () => {
    setEditIndex(null);
    setNewItem({
      name: "",
      shortDescription: "",
      image: "",
      servicePrice: "",
      productPrice: 0,
      duration: "",
      off_percentage: "",
    });
    setPreviewImage("");
    setOpenModal(true);
  };

  const openEditModal = (index) => {
    setEditIndex(index);
    setNewItem(groupItems[index]);
    setPreviewImage(IMAGE_URL + groupItems[index].image);
    setOpenModal(true);
  };

  const handleSaveItem = () => {
    let updated;
    if (editIndex !== null) {
      updated = [...groupItems];
      updated[editIndex] = newItem;
    } else {
      updated = [...groupItems, newItem];
    }
    onChange(updated);
    setOpenModal(false);

    setNewItem({
      name: "",
      shortDescription: "",
      image: "",
      servicePrice: "",
      productPrice: 0,
      duration: "",
      off_percentage: 0,
    });
    setPreviewImage("");

    // Scroll to last slide when new item added
    // if (editIndex === null && sliderRef.current) {
    //   setTimeout(() => {
    //     sliderRef.current.slickGoTo(updated.length - 1);
    //   }, 100);
    // }
    setEditIndex(null);
  };

  const handleDeleteItem = (index) => {
    const updated = groupItems.filter((_, i) => i !== index);
    onChange(updated);
  };

  const delmedia = async (image) => {
    setDeletedFiles(image);
  };

  useEffect(() => {
    if (groupItems.length > 0 && sliderRef.current) {
      // Scroll to last slide
      sliderRef.current.slickGoTo(groupItems.length - 1);
    }
  }, [groupItems]);

  return (
    <Box sx={{ mt: 4 }}>
      <Divider sx={{ my: 3 }} />
      <Typography variant="h6" gutterBottom>
        Group Items*
      </Typography>
      <Box sx={{ py: 2 }}>
        <Slider ref={sliderRef} {...sliderSettings}>
          {groupItems.map((item, index) => (
            <div key={index}>
              <Paper
                sx={{
                  mx: 1,
                  p: 2,
                  position: "relative",
                  cursor: "pointer",
                }}
                // onClick={() => openEditModal(index)}
              >
                <IconButton
                  sx={{ position: "absolute", top: 8, right: 40 }} // Moved left by 32px (40 total for padding)
                  onClick={() => openEditModal(index)}
                >
                  <Edit color="primary" />
                </IconButton>

                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteItem(index);
                    delmedia(item.image);
                  }}
                  sx={{ position: "absolute", top: 8, right: 8 }}
                >
                  <Delete color="error" />
                </IconButton>

                <img
                  crossOrigin="anonymous"
                  src={IMAGE_URL + item.image}
                  alt={item.name}
                  onError={(e) => (e.target.src = "/fallback.jpg")}
                  style={{ width: "100%", height: 100, objectFit: "cover" }}
                />
                <Typography variant="subtitle1" fontWeight="bold" mt={1}>
                  {item.name}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {item.shortDescription}
                </Typography>
                <Typography variant="body2">
                  Duration: {item.duration} /min
                </Typography>
                <Typography variant="body2">
                  Service Price: ₹{item.servicePrice}
                </Typography>
              </Paper>
            </div>
          ))}
          <div>
            <Paper
              onClick={openAddModal}
              sx={{
                mx: 1,
                height: 200,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                cursor: "pointer",
                border: "2px dashed #ccc",
              }}
            >
              <Add fontSize="large" />
              <Typography>Add Group Item</Typography>
            </Paper>
          </div>
        </Slider>
      </Box>

      {/* Modal for Add/Edit Item */}
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" mb={2}>
            {editIndex !== null ? "Edit Group Item" : "Add Group Item"}
          </Typography>

          <TextField
            label="Name"
            value={newItem.name}
            onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
            fullWidth
            margin="normal"
          />

          <TextField
            label="Short Description"
            value={newItem.shortDescription}
            onChange={(e) =>
              setNewItem({ ...newItem, shortDescription: e.target.value })
            }
            fullWidth
            margin="normal"
          />

          <TextField
            label="Service Price"
            type="number"
            value={newItem.servicePrice}
            onChange={(e) =>
              setNewItem({ ...newItem, servicePrice: e.target.value })
            }
            fullWidth
            margin="normal"
          />

          <TextField
            type="number"
            label="Duration"
            value={newItem.duration}
            onChange={(e) =>
              setNewItem({ ...newItem, duration: e.target.value })
            }
            fullWidth
            margin="normal"
          />

          <TextField
            type="number"
            label="Off Percentage"
            value={newItem.off_percentage}
            onChange={(e) =>
              setNewItem({ ...newItem, off_percentage: e.target.value })
            }
            fullWidth
            margin="normal"
          />

          <input
            type="file"
            accept="image/*"
            onChange={async (e) => {
              const file = e.target.files[0];
              if (file) {
                const uploadedPath = await uploadImage(file);
                setNewItem({ ...newItem, image: uploadedPath });
                setPreviewImage(IMAGE_URL + uploadedPath);
              }
            }}
          />
          {previewImage && (
            <Box mt={2}>
              <img
                crossOrigin="anonymous"
                src={previewImage}
                alt="preview"
                style={{ width: "100%", height: 100, objectFit: "cover" }}
              />
            </Box>
          )}

          <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
            <Button variant="outlined" onClick={() => setOpenModal(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveItem}
              disabled={
                !newItem.name ||
                !newItem.shortDescription ||
                !newItem.image ||
                newItem.servicePrice === "" ||
                !newItem.duration
              }
            >
              {editIndex !== null ? "Update" : "Add"}
            </Button>
          </Box>
        </Box>
      </Modal>

      {error && touched && (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default GroupItemsSection;
